import $$ from 'dom7';

/*
 * API compatibility + validation layer for the hardened backend
 * (see API-CHANGES-FOR-APP.md, 18 Aug 2026).
 *
 * The backend used to answer every failure with HTTP 200 and
 * `{"status":"error", "msg":"..."}`. It now returns the matching status code
 * (400 / 403 / 405 / 429 / 503) with the *same body*. jQuery treats anything
 * non-2xx as a failure, so ~120 error paths would have surfaced as the app's
 * generic "No internet connection!" toast instead of the real message.
 *
 * Rather than touch each of the call sites, `$.ajax` is wrapped once here.
 * For our own endpoints a 4xx whose body parses as JSON is handed to the
 * *success* path, so every existing `if (data.status == 'success') ... else
 * show data.msg` block keeps working unchanged. Only timeouts, transport
 * failures and 5xx still reach the error callbacks.
 */

/* Matches our own API regardless of the double slash some call sites build
   (baseURL already ends in "/", several places add another). */
var API_PATH = /app\/+php\//;

var MAX_IMAGE_BYTES = 5 * 1024 * 1024;
var IMAGE_TYPES = [
  'image/jpeg', 'image/jpg', 'image/pjpeg',
  'image/png', 'image/gif', 'image/webp',
];
var IMAGE_EXTENSIONS = /\.(jpe?g|png|gif|webp)$/i;

/* Server rule: 4-64 chars from A-Z a-z 0-9 . _ : - */
var DEVICE_ID_CHARS = /[^A-Za-z0-9._:-]/g;

var app = null;
var blockedNoticeOpen = false;
var sessionExpiring = false;

/* -------------------------------------------------------------------------
   Device install id
   ------------------------------------------------------------------------- */

function sanitizeId(value) {
  return (value || '').replace(DEVICE_ID_CHARS, '').substr(0, 64);
}

function remember(id, source) {
  localStorage.setItem('uuid', id);
  localStorage.setItem('uuidSource', source);
  return id;
}

/* The cordova plugin's native id. Only present once deviceready has fired. */
function cordovaDeviceId() {
  return (window.device && window.device.uuid) || null;
}

/*
 * device-uuid is loaded from a <script> tag (see index.html): its published
 * bundle is ES2020, which neither webpack 4 nor this project's Babel can
 * process, and which an Android 5 WebView cannot parse at all. Everything here
 * therefore treats it as optional.
 */
function deviceUUIDClass() {
  var mod = window.DeviceUUIDModule;
  if (mod && typeof mod.DeviceUUID === 'function') return mod.DeviceUUID;
  if (typeof window.DeviceUUID === 'function') return window.DeviceUUID;
  return null;
}

var parsedAgent;

/*
 * Device and browser details from device-uuid — browser, os, platform, form
 * factor, resolution and so on. Parsed once and cached. On cordova the native
 * `window.device` fields (model, manufacturer, OS version) are merged in.
 * Returns null if neither source is available.
 */
export function getDeviceInfo() {
  if (parsedAgent !== undefined) return parsedAgent;
  parsedAgent = null;

  var Klass = deviceUUIDClass();
  if (Klass) {
    try {
      var agent = new Klass().parse();
      parsedAgent = {
        browser: agent.browser,
        version: agent.version,
        os: agent.os,
        platform: agent.platform,
        language: agent.language,
        resolution: agent.resolution,
        cpuCores: agent.cpuCores,
        isMobile: agent.isMobile,
        isTablet: agent.isTablet,
        isDesktop: agent.isDesktop,
        isAndroid: agent.isAndroid,
        isiPhone: agent.isiPhone,
        isiPad: agent.isiPad,
      };
    } catch (e) {
      parsedAgent = null;
    }
  }

  if (window.device) {
    parsedAgent = parsedAgent || {};
    parsedAgent.cordova = {
      model: window.device.model,
      platform: window.device.platform,
      version: window.device.version,
      manufacturer: window.device.manufacturer,
    };
  }
  return parsedAgent;
}

/*
 * The install id the backend uses to block one device rather than an IP shared
 * by an entire mobile network.
 *
 *   cordova  -> device.uuid, the real hardware id
 *   PWA/web  -> device-uuid's fingerprint, which survives a cleared localStorage
 *   neither  -> a random id, persisted so it at least stays stable
 *
 * A native id already on record wins even before deviceready fires, so a
 * cordova session never switches ids halfway through.
 */
export function getDeviceId() {
  var native = sanitizeId(cordovaDeviceId());
  if (native.length >= 4) return remember(native, 'cordova');

  var stored = localStorage.getItem('uuid');
  var storedId = sanitizeId(stored);
  if (localStorage.getItem('uuidSource') === 'cordova' && storedId.length >= 4) {
    return storedId;
  }

  var Klass = deviceUUIDClass();
  if (Klass) {
    try {
      var fingerprint = sanitizeId(new Klass().get());
      if (fingerprint.length >= 4) return remember(fingerprint, 'device-uuid');
    } catch (e) { /* fall through to the stored / random id */ }
  }

  if (storedId.length >= 4) {
    return storedId === stored ? storedId : remember(storedId, 'random');
  }

  var random = 'ypw-web-';
  for (var i = 0; i < 24; i += 1) {
    random += Math.floor(Math.random() * 36).toString(36);
  }
  return remember(random, 'random');
}

/*
 * Called from the cordova deviceready handler so the native id takes over from
 * whatever was used before the plugin was ready.
 */
export function setCordovaDeviceId(uuid) {
  var id = sanitizeId(uuid);
  if (id.length < 4) return null;
  parsedAgent = undefined;
  return remember(id, 'cordova');
}

/*
 * Everything about the device that is worth re-reporting when it changes: the
 * app build, the OS, the browser and the hardware model. A user who stays
 * signed in for months would otherwise keep whatever they had at login, long
 * after updating the app or their phone.
 */
function deviceStamp(appVersion) {
  var info = getDeviceInfo() || {};
  var native = info.cordova || {};
  return [
    getDeviceId(),
    appVersion || '',
    info.os || '',
    info.browser || '',
    info.version || '',
    native.model || '',
    native.version || '',
  ].join('|');
}

var reportInFlight = false;

/*
 * Fire and forget on purpose: nothing about the login or the page depends on
 * the result. If the endpoint does not exist yet, or answers an error, the user
 * never sees it.
 *
 *   POST <url>
 *     saveDeviceInfo = true
 *     token          = the auth token from OTPver
 *     uuid           = the device install id (also on the query string)
 *     appVersion     = the app build, e.g. "1.0.7"
 *     source         = "cordova" | "device-uuid" | "random" — how uuid was derived
 *     deviceInfo     = JSON string, see getDeviceInfo()
 */
function sendDeviceReport(url, token, appVersion, stamp) {
  var $ = window.jQuery || window.$;
  if (!$ || !url || !token || reportInFlight) return;
  reportInFlight = true;

  var payload;
  try {
    payload = JSON.stringify(getDeviceInfo() || {});
  } catch (e) {
    payload = '{}';
  }

  /* Record the stamp once the server has actually answered — a 4xx counts, so
     a missing endpoint is not re-tried on every page, but a dropped connection
     leaves it unset so the next page tries again. */
  function settle(reached) {
    reportInFlight = false;
    if (reached) localStorage.setItem('deviceReportStamp', stamp);
  }

  $.ajax({
    url: url,
    type: 'POST',
    dataType: 'json',
    cache: false,
    crossDomain: true,
    data: {
      saveDeviceInfo: true,
      token: token,
      uuid: getDeviceId(),
      appVersion: appVersion || '',
      source: localStorage.getItem('uuidSource') || 'unknown',
      deviceInfo: payload,
    },
    success: function () { settle(true); },
    error: function (xhr) { settle(!!(xhr && xhr.status >= 400)); },
  });
}

/*
 * Register this device against the account that just signed in, so the backend
 * can list a user's devices and block one precisely instead of blocking its IP.
 * Always sends — a new sign-in is worth recording even if nothing has changed,
 * and the account behind the device may be a different one.
 */
export function reportDeviceInfo(url, token, appVersion) {
  sendDeviceReport(url, token, appVersion, deviceStamp(appVersion));
}

/*
 * Called on page init. Sends only when something in the stamp actually moved —
 * an app update, an OS upgrade, a new handset — so the usual case is a string
 * compare and no network at all.
 */
export function refreshDeviceInfo(url, appVersion) {
  var token = localStorage.getItem('authToken');
  if (!token) return;
  /* On the native build, hold off until the plugin is up: reporting first would
     send a browser fingerprint that deviceready immediately replaces, and the
     backend would see two devices for one handset. */
  if (window.cordova && !window.device) return;

  var stamp = deviceStamp(appVersion);
  if (localStorage.getItem('deviceReportStamp') === stamp) return;
  sendDeviceReport(url, token, appVersion, stamp);
}

function appendParam(url, key, value) {
  if (!url) return url;
  if (new RegExp('[?&]' + key + '=').test(url)) return url;
  return url + (url.indexOf('?') === -1 ? '?' : '&') +
    key + '=' + encodeURIComponent(value);
}

/* -------------------------------------------------------------------------
   Session / global error handling
   ------------------------------------------------------------------------- */

export function attachApp(instance) {
  app = instance;
}

function toast(text, timeout) {
  if (app && app.toast) {
    app.toast.show({ text: text, closeTimeout: timeout || 4000 });
  }
}

export function clearSession() {
  localStorage.removeItem('authToken');
  localStorage.removeItem('isloggedIN');
  localStorage.removeItem('phone');
  localStorage.removeItem('payment');
}

/*
 * A block ("too many failed attempts") can arrive on any call, signed in or
 * not. Show the server's wording verbatim and do not retry — retrying extends
 * the block. One dialog at a time; a burst of parallel calls must not stack.
 */
function announceBlock(message) {
  var text = message || 'Too many failed attempts. Access has been temporarily blocked.';
  if (!app || !app.dialog || blockedNoticeOpen) return;
  blockedNoticeOpen = true;
  /* Deferred: the call site's own handler runs right after this and closes the
     progress dialog, which would take our alert down with it. */
  setTimeout(function () {
    app.dialog.close();
    app.dialog.alert(text, 'Access blocked', function () {
      blockedNoticeOpen = false;
    });
  }, 0);
}

/*
 * 429: hold every button for the window the server asked for, so an impatient
 * tap does not dig the hole deeper. Scheduled on the next tick because the
 * page's own success handler re-enables `.button` right after this runs.
 */
function holdButtons(seconds) {
  var wait = Math.min(Math.max(parseInt(seconds, 10) || 30, 5), 300);
  setTimeout(function () {
    $$('.button').addClass('disabled');
    setTimeout(function () { $$('.button').removeClass('disabled'); }, wait * 1000);
  }, 0);
}

function announceRateLimit(message, xhr) {
  var retryAfter = null;
  try { retryAfter = xhr.getResponseHeader('Retry-After'); } catch (e) { /* opaque */ }
  holdButtons(retryAfter);
  toast(message || 'Too many attempts. Please try again later.', 5000);
}

/*
 * `changePass` now invalidates the stored token, and an expired or revoked
 * token answers 403. Without this the app would keep using a dead token and
 * every screen would fail in a way that looks like a random logout bug.
 */
function expireSession(message) {
  if (sessionExpiring || !localStorage.getItem('authToken')) return;
  sessionExpiring = true;
  clearSession();
  toast(message || 'Your session has expired, please sign in again.', 4000);
  setTimeout(function () {
    sessionExpiring = false;
    if (!app || !app.views || !app.views.current) return;
    var router = app.views.current.router;
    if (router.currentRoute && router.currentRoute.url === '/login/') return;
    router.navigate('/login/');
  }, 800);
}

function isUnauthorized(body) {
  if (!body) return false;
  if (body.code === 'blocked') return false;
  if (body.status === 'forbidden') return true;
  return /unauthorized/i.test(body.msg || '');
}

/* Runs on every own-API response, success or handled failure. */
function inspect(body, xhr) {
  if (!body || typeof body !== 'object') return;
  var status = (xhr && xhr.status) || 200;
  if (body.code === 'blocked') {
    announceBlock(body.msg);
    return;
  }
  if (status === 429 || body.code === 'rate_limited') {
    announceRateLimit(body.msg, xhr);
    return;
  }
  if (status === 403 && isUnauthorized(body)) {
    expireSession(body.msg);
  }
}

/* -------------------------------------------------------------------------
   The $.ajax wrapper
   ------------------------------------------------------------------------- */

function isOwnApi(url) {
  return typeof url === 'string' && API_PATH.test(url);
}

/* -------------------------------------------------------------------------
   Maintenance mode  (see API-SETTINGS.md)

   Two levels, and the app has to tell them apart:

     maintenance:true, blocking:false  the API still serves. Announce it, but
                                       let the customer carry on.
     maintenance:true, blocking:true   every /app endpoint answers 503 with
                                       code:"maintenance". Nothing works until
                                       Retry-After elapses.

   That 503 is the documented exception to the rule above that 5xx is a
   transport failure: it carries a body worth reading, and retrying it before
   Retry-After only hammers a server mid-migration.
   ------------------------------------------------------------------------- */

var SETTINGS_PATH = /app\/+php\/+settings\.php/;
var maintenanceUntil = 0;
var maintenanceShown = false;

function isSettingsCall(url) {
  return typeof url === 'string' && SETTINGS_PATH.test(url);
}

function retryAfterMs(xhr) {
  var header = xhr && xhr.getResponseHeader && xhr.getResponseHeader('Retry-After');
  var seconds = parseInt(header, 10);
  /* A missing or unparseable header must not mean "retry immediately". */
  if (isNaN(seconds) || seconds <= 0) seconds = 300;
  return seconds * 1000;
}

function maintenanceEl() {
  return document.getElementById('ypw-maintenance');
}

export function hideMaintenance() {
  maintenanceShown = false;
  var el = maintenanceEl();
  if (el) el.classList.add('hidden');
}

/*
 * `blocking` decides whether this can be dismissed. An announcement is a
 * courtesy; a blocking window is the whole app, so offering "continue" there
 * would just lead to a wall of failures.
 */
export function showMaintenance(body, blocking) {
  var el = maintenanceEl();
  if (!el) return;

  /* Launch check and a 503 from an in-flight call race each other at startup.
     Once a blocking window is known, nothing may downgrade the screen to a
     dismissible one -- "Continue anyway" would lead straight into a wall of
     failures. */
  if (maintenanceUntil && Date.now() < maintenanceUntil) blocking = true;

  var message = (body && body.msg) || (body && body.maintenance_message) ||
    'We are currently under maintenance. Please check back later.';
  var eta = (body && body.maintenance_eta) || '';

  var messageEl = el.querySelector('.ypw-maint__message');
  var etaEl = el.querySelector('.ypw-maint__eta');
  if (messageEl) messageEl.textContent = message;
  if (etaEl) {
    etaEl.textContent = eta ? 'Expected back ' + eta : '';
    etaEl.classList[eta ? 'remove' : 'add']('hidden');
  }

  el.classList[blocking ? 'add' : 'remove']('is-blocking');
  el.classList.remove('hidden');

  /* A spinner left running behind this would never be closed: the callbacks
     that would have closed it are deliberately not fired. */
  if (app && app.dialog) {
    try { app.dialog.close(); } catch (e) { /* nothing open */ }
  }
  maintenanceShown = true;
}

/*
 * Asked on launch before the session check, and again whenever a call comes
 * back 503. Deliberately unauthenticated: during maintenance, signing in is
 * exactly what will not work.
 */
export function checkMaintenance(baseUrl, done) {
  var $ = window.jQuery || window.$;
  var url = String(baseUrl || '').replace(/\/+$/, '') + '/app/php/settings.php';

  if (!$ || !$.ajax) { if (done) done(null); return; }

  $.ajax({
    url: url,
    type: 'GET',
    dataType: 'json',
    cache: false,
    crossDomain: true,
    success: function (data) {
      if (data && data.maintenance) {
        showMaintenance(data, !!data.blocking);
        if (!data.blocking) maintenanceUntil = 0;
      } else {
        maintenanceUntil = 0;
        hideMaintenance();
      }
      if (done) done(data);
    },
    /* Unreachable is not the same as "under maintenance" -- saying so would
       turn every flaky connection into a maintenance screen. */
    error: function () { if (done) done(null); },
  });
}

function parseBody(xhr) {
  if (!xhr) return null;
  if (xhr.responseJSON && typeof xhr.responseJSON === 'object') return xhr.responseJSON;
  var text = xhr.responseText;
  if (!text || typeof text !== 'string') return null;
  try {
    var parsed = JSON.parse(text);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (e) {
    return null;
  }
}

export function installApiCompat() {
  var $ = window.jQuery || window.$;
  if (!$ || !$.ajax || $.ajax.ypwWrapped) return;

  var nativeAjax = $.ajax;

  function wrapped(url, settings) {
    var options;
    if (url && typeof url === 'object') {
      options = $.extend({}, url);
    } else {
      options = $.extend({}, settings);
      options.url = url;
    }

    if (!isOwnApi(options.url)) return nativeAjax.call($, options);

    /* Sent as a query parameter rather than the X-Device-Id header on purpose:
       a custom header would turn these simple cross-origin POSTs into
       preflighted ones. The backend accepts `uuid` as a GET or POST field. */
    options.url = appendParam(options.url, 'uuid', getDeviceId());

    var success = options.success;
    var error = options.error;
    delete options.success;
    delete options.error;

    var dfd = $.Deferred();
    var pending = null;

    function send(settings, allowMethodRetry) {
      var jqXHR = nativeAjax.call($, settings);
      pending = jqXHR;

      jqXHR.done(function (data, textStatus, xhr) {
        inspect(data, xhr);
        if (success) success(data, textStatus, xhr);
        dfd.resolve(data, textStatus, xhr);
      });

      jqXHR.fail(function (xhr, textStatus, errorThrown) {
        /* Several reads still go out as GET. If one of them is now POST-only
           the server answers 405 — resend it once as a POST with the query
           string as the body rather than showing the user a dead end. */
        if (allowMethodRetry && xhr && xhr.status === 405) {
          var retry = $.extend({}, settings);
          var split = (settings.url || '').split('?');
          retry.type = 'POST';
          retry.url = split[0];
          retry.data = split[1] || settings.data;
          send(retry, false);
          return;
        }

        /* 503 + code:"maintenance" is an answer, not a dropped connection.
           Neither callback runs: the screen is the terminal state, and letting
           call sites also report "No internet connection!" behind it would be
           both wrong and noisy. */
        if (xhr && xhr.status === 503) {
          var down = parseBody(xhr);
          if (down && (down.code === 'maintenance' || down.maintenance)) {
            maintenanceUntil = Date.now() + retryAfterMs(xhr);
            showMaintenance(down, true);
            return;
          }
        }

        var body = xhr && xhr.status >= 400 && xhr.status < 500 ? parseBody(xhr) : null;
        if (body) {
          /* A real business/validation failure that merely travels with a 4xx
             now. Hand it to the success path with the body untouched. */
          inspect(body, xhr);
          if (success) success(body, 'success', xhr);
          dfd.resolve(body, 'success', xhr);
          return;
        }
        if (error) error(xhr, textStatus, errorThrown);
        dfd.reject(xhr, textStatus, errorThrown);
      });
    }

    /* Retry-After has not elapsed and this is not the settings call, so there
       is nothing to learn from sending it. */
    if (maintenanceUntil && Date.now() < maintenanceUntil && !isSettingsCall(options.url)) {
      if (!maintenanceShown) showMaintenance(null, true);
      var blocked = dfd.promise();
      blocked.abort = function () { return blocked; };
      blocked.getResponseHeader = function () { return null; };
      return blocked;
    }

    var isGet = !options.type || String(options.type).toUpperCase() === 'GET';
    send(options, isGet);

    var promise = dfd.promise();
    promise.abort = function () { if (pending) pending.abort(); return promise; };
    promise.getResponseHeader = function (name) {
      return pending ? pending.getResponseHeader(name) : null;
    };
    return promise;
  }

  wrapped.ypwWrapped = true;
  $.ajax = wrapped;
}

/* -------------------------------------------------------------------------
   Client-side mirrors of the server's validation rules
   ------------------------------------------------------------------------- */

/* Server: JPG / PNG / GIF / WEBP, genuinely decodable, 1 byte - 5 MB. */
export function imageError(file) {
  if (!file) return 'Please attach the payment screenshot.';
  var type = (file.type || '').toLowerCase();
  var namedRight = IMAGE_EXTENSIONS.test(file.name || '');
  if (type ? IMAGE_TYPES.indexOf(type) === -1 : !namedRight) {
    return 'Only JPG, PNG, GIF or WEBP images are accepted.';
  }
  if (!file.size) return 'That file is empty, please pick another screenshot.';
  if (file.size > MAX_IMAGE_BYTES) {
    return 'Image must be under 5 MB — yours is ' +
      (file.size / (1024 * 1024)).toFixed(1) + ' MB. Please compress it and try again.';
  }
  return null;
}

/* Server: 8-22 digits, numeric only. */
export function utrError(value) {
  var utr = (value || '').trim();
  if (!utr) return 'Please enter your UTR number!';
  if (!/^[0-9]{8,22}$/.test(utr)) {
    return 'The UTR number must be 8 to 22 digits, numbers only.';
  }
  return null;
}

/* Server: 6-20 digits, numeric only. */
export function accountNumberError(value) {
  var account = (value || '').trim();
  if (!account) return 'Please enter the account number.';
  if (!/^[0-9]{6,20}$/.test(account)) {
    return 'The account number must be 6 to 20 digits, numbers only.';
  }
  return null;
}

/* Server: 4-20 letters and digits. */
export function ifscError(value) {
  var ifsc = (value || '').trim();
  if (!ifsc) return 'Please enter the IFSC or SWIFT code.';
  if (!/^[A-Za-z0-9]{4,20}$/.test(ifsc)) {
    return 'The IFSC / SWIFT code must be 4 to 20 letters and digits.';
  }
  return null;
}

/* Server: 8-128 chars with at least one letter and one number. Existing
   passwords still sign in — this applies to new and changed passwords. */
export function passwordError(value) {
  var password = value || '';
  if (password.length < 8 || password.length > 128) {
    return 'Your password must be 8 to 128 characters long.';
  }
  if (!/[A-Za-z]/.test(password) || !/[0-9]/.test(password)) {
    return 'Your password must contain at least one letter and one number.';
  }
  return null;
}
